import {
  archiveHabit,
  completeGoalPart,
  editGoal,
  endGoal,
  setGoalParts,
  setHabitFloor,
  setHabitPlan,
  startGoal,
  createHabit,
  correctEntry,
  createTask,
  deleteEntry,
  habitGrid,
  habitRhythm,
  habitsAwaitingFloor,
  habitsAwaitingPlan,
  homeStats,
  jatraView,
  ledger,
  ledgerSummary,
  finishedGoals,
  flavourOf,
  lifetime,
  setProfilePath,
  shiftDate,
  weeklyReview,
  loggedTracks,
  logEntry,
  recentTopics,
  recentTracks,
  reviewPlan,
  recordReading,
  correctReading,
  deleteReading,
  series,
  reorderHabits,
  toggleHabit,
  toggleTask,
  todayView,
  weekStrip,
  yearGrid,
} from '@aj/core';
import type { CompletionLevel, EntryKind, Schedule, StudentPath, StudyDate } from '@aj/core';
import { useMemo } from 'react';

import { useAppData } from './AppDataProvider.js';
import { useI18n } from '../providers/I18nProvider.js';
import type { CoachContext } from './coachFlows.js';

/**
 * What screens actually import.
 *
 * Components never touch `reduce`, `db`, or `DerivedState` — they call these
 * and read selector output. That indirection is what lets the reducer, the
 * state shape, or the storage engine change without editing a single screen.
 */

export function useActions() {
  const { actions, ruleset } = useAppData();

  return useMemo(
    () => ({
      toggleHabit: (habitId: string, date?: StudyDate, level?: CompletionLevel) =>
        actions.run((state, ctx) => toggleHabit(state, ctx, ruleset, habitId, date, level)),

      createHabit: (input: {
        name: string;
        schedule?: Schedule;
        icon?: string;
        floor?: string;
        anchor?: string;
        place?: string;
      }) =>
        actions.run((_, ctx) =>
          createHabit(ctx, { ...input, schedule: input.schedule ?? { kind: 'daily' } }),
        ),

      archiveHabit: (habitId: string) =>
        actions.run((state, ctx) => archiveHabit(state, ctx, habitId)),

      reorderHabits: (habitIds: readonly string[]) =>
        actions.run((state, ctx) => reorderHabits(state, ctx, habitIds)),

      toggleTask: (taskId: string) => actions.run((state, ctx) => toggleTask(state, ctx, taskId)),

      createTask: (input: { name: string; track?: string; dueDate?: StudyDate }) =>
        actions.run((_, ctx) => createTask(ctx, input)),

      logEntry: (input: {
        track: string;
        minutes: number;
        kind?: EntryKind;
        intention?: string;
      }) =>
        actions.run((_, ctx) => logEntry(ctx, ruleset, input)),

      /**
       * Correcting is NOT gated by the backfill window, unlike ticking a past
       * day. Claiming a habit you did not do is self-deception and the window
       * guards it; fixing a number you typed wrong is the opposite, and a
       * window there would only be enforcing a dishonest ledger.
       */
      correctEntry: (
        entryId: string,
        minutes: number,
        patch?: { track?: string; kind?: EntryKind },
      ) => actions.run((state, ctx) => correctEntry(state, ctx, entryId, minutes, patch)),

      deleteEntry: (entryId: string) =>
        actions.run((state, ctx) => deleteEntry(state, ctx, entryId)),

      /** Defines the measure too, when the name is new — see `recordReading`. */
      recordReading: (input: Parameters<typeof recordReading>[2]) =>
        actions.run((state, ctx) => recordReading(state, ctx, input)),

      correctReading: (readingId: string, value: number, outOf?: number) =>
        actions.run((state, ctx) => correctReading(state, ctx, readingId, value, outOf)),

      deleteReading: (readingId: string) =>
        actions.run((state, ctx) => deleteReading(state, ctx, readingId)),

      /**
       * Everything a habit gets beyond its name.
       *
       * Separate from `createHabit` on purpose. Intake asks for a name and
       * nothing else, because a floor typed on day one is a solution to a
       * problem the student has not had yet and a plan typed before they have
       * tried is a guess. These arrive later, from the coach, at the moment a
       * real experience has made the question answerable.
       */
      setHabitPlan: (habitId: string, plan: { anchor?: string; place?: string }) =>
        actions.run((state, ctx) => setHabitPlan(state, ctx, habitId, plan)),

      setHabitFloor: (habitId: string, floor: string) =>
        actions.run((state, ctx) => setHabitFloor(state, ctx, habitId, floor)),

      /** The one onboarding question about the person. Picks the register and
       *  the preset pack — see `flavour.ts`. */
      setProfilePath: (path: StudentPath) =>
        actions.run((_, ctx) => setProfilePath(ctx, path)),

      startGoal: (input: Parameters<typeof startGoal>[1]) =>
        actions.run((_, ctx) => startGoal(ctx, input)),

      editGoal: (goalId: string, patch: Parameters<typeof editGoal>[3]) =>
        actions.run((state, ctx) => editGoal(ctx, state, goalId, patch)),

      setGoalParts: (goalId: string, parts: string[]) =>
        actions.run((state, ctx) => setGoalParts(ctx, state, goalId, parts)),

      completeGoalPart: (goalId: string, part: string) =>
        actions.run((state, ctx) => completeGoalPart(ctx, state, goalId, part)),

      endGoal: (goalId: string, status: 'completed' | 'abandoned') =>
        actions.run((state, ctx) => endGoal(ctx, state, goalId, status)),
    }),
    [actions, ruleset],
  );
}

/** Everything the Home screen renders, in one memoised call. */
export function useTodayView() {
  const { state, today, localHour, features, ruleset } = useAppData();
  return useMemo(
    () => todayView(state, today, localHour, features, ruleset),
    [state, today, localHour, features, ruleset],
  );
}

export function useWeekStrip() {
  const { state, today } = useAppData();
  return useMemo(() => weekStrip(state, today), [state, today]);
}

/**
 * The habit × date wall chart.
 *
 * `days` is a caller override because the window is a layout decision, not a
 * product one: the desktop dashboard shows whatever fits its width, while the
 * flag stays the default everywhere else.
 */
export function useHabitGrid(days?: number) {
  const { state, today, features } = useAppData();
  const span = days ?? features.progress.gridDays;
  return useMemo(() => habitGrid(state, today, span), [state, today, span]);
}

/**
 * Track names to offer when logging, most recent first.
 *
 * Separate from `useTodayView` because it is not a property of today — the
 * chips a student is offered come from the whole log, and recomputing them
 * inside the Home selector would tie a picker's contents to the day being
 * viewed.
 */
export function useRecentTracks() {
  const { state, features } = useAppData();
  return useMemo(
    () => recentTracks(state, features.home.trackSuggestions),
    [state, features.home.trackSuggestions],
  );
}

/**
 * The ledger, and the tracks that can filter it.
 *
 * `days` counts days that HAVE something in them, so paging always yields
 * more to read rather than more blanks — see `ledger` in core.
 */
export function useLedger(days: number, track?: string) {
  const { state } = useAppData();
  return useMemo(
    () => ledger(state, days, track === undefined ? {} : { track }),
    [state, days, track],
  );
}

export function useLoggedTracks() {
  const { state } = useAppData();
  return useMemo(() => loggedTracks(state), [state]);
}

/** Everything Jatra needs. The grid comes separately via `useHabitGrid`. */
export function useJatraView() {
  const { state, today, features, ruleset } = useAppData();
  return useMemo(
    () => jatraView(state, today, features, ruleset),
    [state, today, features, ruleset],
  );
}

/** The stat band around the wall chart. Desktop only — see `homeStats`. */
export function useHomeStats() {
  const { state, today, features } = useAppData();
  return useMemo(() => homeStats(state, today, features), [state, today, features]);
}

/** Every measure with readings, most recently updated first. */
export function useSeries() {
  const { state } = useAppData();
  return useMemo(() => series(state), [state]);
}

/** What to revise today, or null when nothing is due. */
export function useReviewPlan() {
  const { state, today, ruleset } = useAppData();
  return useMemo(() => reviewPlan(state, today, ruleset), [state, today, ruleset]);
}

/**
 * Labels already used on a track.
 *
 * Offered as chips when starting a session so reuse is a tap: "ch.4" and
 * "chapter 4" typed on different days would otherwise become two topics with
 * two review schedules, and the scheduler would quietly degrade.
 */
export function useRecentTopics(track: string, limit = 6) {
  const { state } = useAppData();
  return useMemo(() => recentTopics(state, track, limit), [state, track, limit]);
}

/** The year at a glance. Weeks of 7, oldest first. */
export function useYearGrid(weeks = 53) {
  const { state, today } = useAppData();
  return useMemo(() => yearGrid(state, today, weeks), [state, today, weeks]);
}

/**
 * What the coach can usefully ask about right now.
 *
 * Each of these is a field deliberately absent from intake, waiting for the
 * experience that makes it answerable. Returning at most one of each is the
 * point: the coach's budget allows one interruption a session, so offering it
 * a queue would only invite it to be ignored.
 */
export function useCaptureTargets(): CoachContext {
  const { state, today } = useAppData();
  const i18n = useI18n();

  return useMemo(() => {
    const context: CoachContext = {};

    const planId = habitsAwaitingPlan(state)[0];
    const rhythm = planId === undefined ? null : habitRhythm(state, planId);
    const planHabit = planId === undefined ? undefined : state.habits[planId];
    if (planId !== undefined && rhythm && planHabit) {
      // A bare hour ("19") means nothing; the student thinks in "evening" and
      // in prayer times. Formatting through the locale keeps the numeral
      // Bengali when the app is.
      const at = new Date();
      at.setHours(rhythm.hour, 0, 0, 0);
      context.plan = {
        habitId: planId,
        name: planHabit.name,
        time: i18n.formatDate(at.getTime(), { hour: 'numeric' }),
      };
    }

    const floorId = habitsAwaitingFloor(state, today)[0];
    const floorHabit = floorId === undefined ? undefined : state.habits[floorId];
    if (floorId !== undefined && floorHabit) {
      context.floor = { habitId: floorId, name: floorHabit.name };
    }

    return context;
  }, [state, today, i18n]);
}

/** What the ledger adds up to over a window. Khata's summary strip. */
export function useLedgerSummary(days: number) {
  const { state, today } = useAppData();
  return useMemo(() => ledgerSummary(state, shiftDate(today, -days)), [state, today, days]);
}

/** Goals already finished — the centrepiece of Me. */
export function useFinishedGoals() {
  const { state } = useAppData();
  return useMemo(() => finishedGoals(state), [state]);
}

/** The numbers that only ever go up. */
export function useLifetime() {
  const { state } = useAppData();
  return useMemo(() => lifetime(state), [state]);
}

/** The week just gone, or null when there is not yet a week to review. */
export function useWeeklyReview() {
  const { state, today } = useAppData();
  return useMemo(() => weeklyReview(state, today), [state, today]);
}

/**
 * The register the app speaks in — see `flavour.ts`.
 *
 * A hook rather than a prop drilled from the root, because it is read in two
 * unrelated corners (the rank chip on আমি, the path on যাত্রা) and threading
 * it through everything between them would be a lot of plumbing for an
 * adjective.
 */
export function useFlavour() {
  const { state } = useAppData();
  return useMemo(() => flavourOf(state), [state]);
}
